import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API_BASE } from '../utils/api';

function AdminNetworkAccessManager() {
const [configuredEntries, setConfiguredEntries] = useState([]);
const [managedEntries, setManagedEntries] = useState([]);
const [currentAddress, setCurrentAddress] = useState('');
const [newEntry, setNewEntry] = useState('');
const [loading, setLoading] = useState(true);
const [saving, setSaving] = useState(false);
const [message, setMessage] = useState('');
const [error, setError] = useState('');
const token = localStorage.getItem('token');

const headers = { Authorization: `Bearer ${token}` };

const applySnapshot = (snapshot) => {
setConfiguredEntries(snapshot.configuredEntries || []);
setManagedEntries(snapshot.managedEntries || []);
setCurrentAddress(snapshot.currentAddress || 'unknown');
};

useEffect(() => {
const load = async () => {
try {
const response = await axios.get(`${API_BASE}/admin/ip-allowlist`, { headers });
applySnapshot(response.data);
} catch (requestError) {
setError(requestError.response?.data?.message || 'Failed to load the admin IP allowlist.');
} finally {
setLoading(false);
}
};
load();
// The token is fixed for the lifetime of this mounted admin view.
// eslint-disable-next-line react-hooks/exhaustive-deps
}, []);

const addEntry = () => {
const value = newEntry.trim();
if (!value) return;
if ([...configuredEntries, ...managedEntries].some(entry => entry.toLowerCase() === value.toLowerCase())) {
setError('That address or range is already allowed.');
return;
}
setManagedEntries([...managedEntries, value]);
setNewEntry('');
setError('');
setMessage('');
};

const save = async () => {
setSaving(true);
setError('');
setMessage('');
try {
const response = await axios.put(
`${API_BASE}/admin/ip-allowlist`,
{ entries: managedEntries },
{ headers }
);
applySnapshot(response.data);
setMessage('Admin IP allowlist updated.');
} catch (requestError) {
setError(requestError.response?.data?.message || 'Failed to update the admin IP allowlist.');
} finally {
setSaving(false);
}
};

if (loading) return <div style={styles.status}>Loading network access rules…</div>;

return (
<div style={styles.container}>
  <p style={styles.intro}>
    Allow exact IPv4 or IPv6 addresses and CIDR ranges such as <code>192.168.20.0/24</code> or <code>10.20.0.0/16</code>.
  </p>
  <div style={styles.current}>Current connection address: <strong>{currentAddress}</strong></div>

  {error && <div style={styles.error}>{error}</div>}
  {message && <div style={styles.success}>{message}</div>}

  <section style={styles.section}>
    <h3>Configured break-glass entries</h3>
    <p style={styles.hint}>These entries come from server configuration and cannot be removed here.</p>
    <div style={styles.chips}>
      {configuredEntries.map(entry => <span key={entry} style={styles.protectedChip}>{entry}</span>)}
    </div>
  </section>

  <section style={styles.section}>
    <h3>Admin-managed entries</h3>
    <div style={styles.addRow}>
      <input
        aria-label="IP address or CIDR range"
        value={newEntry}
        onChange={(event) => setNewEntry(event.target.value)}
        onKeyDown={(event) => event.key === 'Enter' && addEntry()}
        placeholder="203.0.113.25, 192.168.20.0/24, or 2001:db8::/64"
        style={styles.input}
      />
      <button onClick={addEntry} style={styles.addButton}>Add</button>
    </div>

    <div style={styles.chips}>
      {managedEntries.length === 0 && <span style={styles.hint}>No additional entries configured.</span>}
      {managedEntries.map(entry => (
        <span key={entry} style={styles.managedChip}>
          {entry}
          <button
            aria-label={`Remove ${entry}`}
            onClick={() => setManagedEntries(managedEntries.filter(value => value !== entry))}
            style={styles.removeButton}
          >×</button>
        </span>
      ))}
    </div>

    <button onClick={save} disabled={saving} style={styles.saveButton}>
      {saving ? 'Saving…' : 'Save allowlist'}
    </button>
    <p style={styles.hint}>The server rejects changes that would exclude your current connection address.</p>
  </section>
</div>
);
}

const styles = {
container: { maxWidth: '850px' },
intro: { color: '#475569', lineHeight: 1.6 },
current: { padding: '12px 16px', background: '#eef2ff', borderRadius: '8px', margin: '16px 0' },
section: { border: '1px solid #e2e8f0', borderRadius: '10px', padding: '20px', marginBottom: '18px' },
hint: { color: '#64748b', fontSize: '14px' },
chips: { display: 'flex', flexWrap: 'wrap', gap: '10px', margin: '14px 0' },
protectedChip: { background: '#e2e8f0', color: '#334155', padding: '8px 12px', borderRadius: '18px' },
managedChip: { display: 'flex', alignItems: 'center', gap: '8px', background: '#667eea', color: 'white', padding: '8px 10px 8px 12px', borderRadius: '18px' },
removeButton: { border: 0, background: 'rgba(255,255,255,.25)', color: 'white', borderRadius: '50%', width: '22px', height: '22px', cursor: 'pointer' },
addRow: { display: 'flex', gap: '10px', marginTop: '14px' },
input: { flex: 1, padding: '11px', border: '1px solid #cbd5e1', borderRadius: '6px' },
addButton: { padding: '10px 20px', border: 0, borderRadius: '6px', background: '#475569', color: 'white', cursor: 'pointer' },
saveButton: { padding: '11px 20px', border: 0, borderRadius: '6px', background: '#16a34a', color: 'white', cursor: 'pointer', fontWeight: 600 },
error: { padding: '12px', marginBottom: '12px', borderRadius: '6px', background: '#fee2e2', color: '#991b1b' },
success: { padding: '12px', marginBottom: '12px', borderRadius: '6px', background: '#dcfce7', color: '#166534' },
status: { padding: '30px', color: '#64748b' }
};

export default AdminNetworkAccessManager;
